import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { ContentStateService } from '../content-state.service';
import { getBlob, getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { AudioVoiceSettings, AuthService, UserConfiguration } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { TranscriptionPayload } from 'src/app/shared/components/recorders/audio-recorder/audio-recorder.dto';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { StorageService } from 'src/app/storage.service';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { ContentUtilsService } from '../content-utils.service';
import { ServerContent } from '../content.dto';
import { Chunk, ChunkAudio, ChunkEdition, ChunkType, CopyImageAndCreateChunkData, ServerChunk } from './chunk.dto';
import { generateObjectId } from 'src/app/shared/utils/object-id.utils';

// Define a type for our state change command for better type safety
export interface ChunkStateCommand {
  chunkId: string;
  editMode: string;
}

export interface CreateChunkResponse {
  chunk: ServerChunk;
  content: ServerContent;
}

export interface GenerateAudioData {
  chunkId: string;
  contentId?: string;
  version: number;
  linkPrefix: string;
  editions: ChunkEdition[];
  audios: ChunkAudio[];
  targetLanguage: string;
  voice?: string;
  language?: string;
  user?: unknown;
  configuration?: {
    TTSAIModel: {
      en: string;
      fr: string;
    };
    audioConfiguration: {
      en: AudioVoiceSettings;
      fr: AudioVoiceSettings;
    };
  };
}

export interface TranslateChunkData {
  chunkId: string;
  contentId: string;
  version: number;
  editions: ChunkEdition[];
  targetLanguage: string;
  type: ChunkType;
  user?: unknown;
}

export interface EditChunkData {
  id: string;
  edition: ChunkEdition;
  content: string;
  contentID: string;
  language: string;
  notificationToken?: string;
  type?: string;
  title?: string;
  status?: string;
}

export interface DeleteChunkData {
  chunkID: string;
  contentID: string;
  type: string;
  contentFileID?: string;
}

export interface GenerateSpeechData {
  text: string;
  languageCode: string;
  audioConfig: AudioVoiceSettings;
  provider: string;
  user?: unknown;
}

export interface TranscribeAudioData {
  fileLink: string;
  language: string;
  model: 'chirp3' | 'medicalDictation' | 'medicalConversation';
}

let httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ChunkService {
  url;

  private chunkStateSource = new Subject<ChunkStateCommand>();
  chunkState$ = this.chunkStateSource.asObservable();

  private currentModeSource = new Subject();
  currentMode$ = this.currentModeSource.asObservable();

  private chunkEditEventSource = new Subject();
  chunkEditEvent$ = this.chunkEditEventSource.asObservable();

  private chunkAudioActionSource = new Subject();
  chunkAudioAction$ = this.chunkAudioActionSource.asObservable();

  private readonly _chunkToFocusOnSource = new BehaviorSubject<string | null>(null);
  readonly chunkToFocusOn$ = this._chunkToFocusOnSource.asObservable();

  private chunkCreatedOnCacheSource = new Subject();
  chunkCreatedOnCache$ = this.chunkCreatedOnCacheSource.asObservable();

  private liveTranscriptionStartedSource = new BehaviorSubject(false);
  liveTranscriptionStarted$ = this.liveTranscriptionStartedSource.asObservable();

  private liveTranscriptionChunkIdSource = new BehaviorSubject<string | null>(null);
  liveTranscriptionChunkId$ = this.liveTranscriptionChunkIdSource.asObservable();

  private liveTranscriptionDataSource = new Subject<{ text: string; isFinal: boolean }>();
  liveTranscriptionData$ = this.liveTranscriptionDataSource.asObservable();

  storage;

  private chunkTTSFileSource = new Subject();
  chunkTTSFileAction$ = this.chunkTTSFileSource.asObservable();

  constructor(
    private http: HttpClient,
    private env: EnvService,
    private toast: ToastService,
    private translate: TranslateService,
    private authService: AuthService,
    private storageService: StorageService,
    private contentUtilsService: ContentUtilsService
  ) {
    this.url = this.env.apiUrl;
    this.storage = inject(Storage);
  }

  private injector = inject(Injector);

  private get contentStateService(): ContentStateService {
    return this.injector.get(ContentStateService);
  }

  setChunkEditMode(chunkId: string, mode: string): void {
    this.chunkStateSource.next({ chunkId, editMode: mode });
  }

  passChunkEditEvent(event) {
    this.chunkEditEventSource.next(event);
  }

  passChunkCreatedOnCacheEvent(event?) {
    this.chunkCreatedOnCacheSource.next(event);
  }

  passChunkMode(mode) {
    this.currentModeSource.next(mode);
  }

  setLiveTranscriptionStartedValue(value) {
    this.liveTranscriptionStartedSource.next(value);
  }

  async setLiveTranscriptionChunkId(id: string | null) {
    this.liveTranscriptionChunkIdSource.next(id);
  }

  passLiveTranscriptionData(text: string, isFinal: boolean) {
    this.liveTranscriptionDataSource.next({ text, isFinal });
  }

  passTTSFile(file) {
    this.chunkTTSFileSource.next(file);
  }

  setChunkToFocusOn(chunkId: string | null): void {
    this._chunkToFocusOnSource.next(chunkId);
  }

  createChunk(data): Observable<CreateChunkResponse[]> {
    data.chunks = data.chunks.map((element) => {
      // Add missing parent context from the state service
      element._id = element._id || generateObjectId();
      element.contentID = element.contentID || this.contentStateService.contentId;
      element.courseID = element.courseID || this.contentStateService.courseId;
      element.noteID = element.noteID || this.contentStateService.noteId;
      element.blogID = element.blogID || this.contentStateService.blogId;
      element.section = element.section || this.contentStateService.section;
      element.lecture = element.lecture || this.contentStateService.lecture;
      element.contentName = element.contentName || this.contentStateService.contentName;
      element.contentType = element.contentType || this.contentStateService.contentType;

      const userData = JSON.parse(localStorage.getItem('userData'));
      element.user = {
        id: userData.id,
        fullName: userData.firstName + ' ' + userData.lastName,
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
      };
      return element;
    });

    return this.http.post<CreateChunkResponse[]>(this.url + 'chunks/create', data, httpOptions).pipe(
      tap(async (res: CreateChunkResponse[]) => {
        let allOptimistic = true;
        for (let i = 0; i < res.length; i++) {
          const { chunk, content } = res[i];
          const localChunks = await this.storageService.find('chunks', [chunk._id]);
          if (localChunks && localChunks.length > 0) {
            const localChunk = localChunks[0];
            const mergedChunk = {
              ...chunk,
              editions: localChunk.editions,
              mains: localChunk.mains || chunk.mains,
              main: localChunk.main || chunk.main,
              syncState: 'synced',
            };
            await this.storageService.store('chunks', mergedChunk);
          } else {
            allOptimistic = false;
            await this.storageService.store('chunks', { ...chunk, syncState: 'synced' });
          }
          this._chunkToFocusOnSource.next(chunk._id);
          
          if (i === res.length - 1 && !allOptimistic) {
            const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
            this.storageService.store('contents', composedContent);
          }
        }
      }),
      catchError(async (error) => {
        // If the request fails, mark the chunks as failed in local storage
        if (data.chunks && Array.isArray(data.chunks)) {
          for (const c of data.chunks) {
            const localChunks = await this.storageService.find('chunks', [c._id || c.alternateID]);
            if (localChunks && localChunks.length > 0) {
              const localChunk = localChunks[0];
              await this.storageService.store('chunks', { ...localChunk, syncState: 'failed' });
              this.contentStateService.updateChunkLocally(localChunk._id, { syncState: 'failed' });
            }
          }
        }
        throw error;
      })
    );
  }

  createUploadedChunk(data): Observable<any> {
    data.contentID = data.contentID || this.contentStateService.contentId;
    data.courseID = data.courseID || this.contentStateService.courseId;
    data.noteID = data.noteID || this.contentStateService.noteId;
    data.blogID = data.blogID || this.contentStateService.blogId;
    data.section = data.section || this.contentStateService.section;
    data.lecture = data.lecture || this.contentStateService.lecture;
    data.contentName = data.contentName || this.contentStateService.contentName;
    data.contentType = data.contentType || this.contentStateService.contentType;
    return this.http.post(this.url + 'submit_chunk_file', data, httpOptions);
  }

  finalizeLiveTranscription(data): Observable<any> {
    data.contentID = data.contentID || this.contentStateService.contentId;
    data.courseID = data.courseID || this.contentStateService.courseId;
    data.noteID = data.noteID || this.contentStateService.noteId;
    data.blogID = data.blogID || this.contentStateService.blogId;
    data.section = data.section || this.contentStateService.section;
    data.lecture = data.lecture || this.contentStateService.lecture;
    data.contentName = data.contentName || this.contentStateService.contentName;
    data.contentType = data.contentType || this.contentStateService.contentType;

    const userData = JSON.parse(localStorage.getItem('userData'));
    data.user = {
      id: userData.id,
      fullName: userData.firstName + ' ' + userData.lastName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    };
    return this.http.post(this.url + 'chunks/finalize-transcription', data, httpOptions);
  }
  getEncryptionStatus(data): Observable<any> {
    return this.http.post(this.url + 'contents/get-encryption-status/', data, httpOptions);
  }
  encryptContent(data): Observable<any> {
    return this.http.post(this.url + 'contents/encrypt-content/', data, httpOptions);
  }

  decryptContent(data): Observable<any> {
    return this.http.post(this.url + 'contents/decrypt-content/', data, httpOptions);
  }
  editVideoDescription(data): Observable<any> {
    return this.http.post(this.url + 'chunks/edit-description', data, httpOptions);
  }

  editChunk(data: EditChunkData): Observable<CreateChunkResponse> {
    return this.http.post(this.url + 'chunks/editChunk', data, httpOptions).pipe(
      tap(async (res: CreateChunkResponse) => {
        const { chunk, content } = res;
        await this.storageService.store('chunks', chunk);
        const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
        this.storageService.store('contents', composedContent);
      })
    );
  }

  initializeLiveTranscription(data): Observable<any> {
    data.contentID = data.contentID || this.contentStateService.contentId;
    data.courseID = data.courseID || this.contentStateService.courseId;
    data.noteID = data.noteID || this.contentStateService.noteId;
    data.blogID = data.blogID || this.contentStateService.blogId;
    data.section = data.section || this.contentStateService.section;
    data.lecture = data.lecture || this.contentStateService.lecture;
    data.contentName = data.contentName || this.contentStateService.contentName;
    data.contentType = data.contentType || this.contentStateService.contentType;
    return this.http.post(this.url + 'chunks/initialize-transcription', data, httpOptions);
  }
  getChunk(id): Observable<any> {
    return this.http.get(this.url + 'chunks/?id=' + id, httpOptions);
  }
  uploadChunkAudio(data) {
    this.chunkAudioActionSource.next(data);
  }

  restartMultipleChunkCreationProcess(inputChunks) {
    return new Promise(async (resolve, reject) => {
      let results = [];
      for (let inputChunk of inputChunks) {
        try {
          results.push(await this.restartChunkCreationProcess(inputChunk));
        } catch (err) {
          console.error(err);
          reject();
        }
      }
      resolve(results);
    });
  }

  restartChunkCreationProcess(inputChunk) {
    return new Promise((resolve, reject) => {
      inputChunk.chunkParams.alternateID = inputChunk?._id;
      this.createChunk({ chunks: [inputChunk.chunkParams] }).subscribe(
        (results) => {
          for (let res of results) {
            let { chunk, content } = res;
            this.storageService.update('chunks', inputChunk?._id, { _id: chunk?._id, error: null });
            resolve(chunk);
          }
        },
        (err) => {
          console.error(err);
          this.storageService.update('chunks', inputChunk?._id, { error: 'creation' });
          reject();
        }
      );
    });
  }

  getContentID(chunkInfo) {
    return new Promise(async (resolve, reject) => {
      this.storageService
        .find('courses', [chunkInfo.courseID])
        .then((courses) => {
          let course = courses[0];
          let section = course.sections.find((section) => section._id == chunkInfo.section._id);
          let lecture = section.lectures.find((lecture) => lecture._id == chunkInfo.lecture._id);
          resolve(lecture.content_id);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  arrayBufferToBase64(buffer) {
    var binary = '';
    var bytes = new Uint8Array(buffer);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  getMediaData(link) {
    if (link.includes('data:audio/mpeg;base64')) {
      return of(link);
    }
    return this.http.get(link, { responseType: 'arraybuffer' }).pipe(
      map((file: ArrayBuffer) => {
        return this.arrayBufferToBase64(file);
      })
    );
  }

  createChunksInIndex(chunkInfos, insertBelowID?: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const userData = localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')) : undefined;
        const chunksData = chunkInfos.map((chunkInfo) => {
          const chunkID = chunkInfo._id || generateObjectId();
          const firstEditionID = generateObjectId();
          return {
            _id: chunkID,
            private: true,
            type: chunkInfo.type,
            title: chunkInfo.title,
            chunkParams: chunkInfo,
            editions: [
              {
                createdAt: new Date(),
                _id: firstEditionID,
                content: chunkInfo.content,
                type: chunkInfo.type,
                language: chunkInfo.language,
                codeLanguage: chunkInfo.codeLanguage,
              },
            ],
            hasAudio: false,
            audios: [],
            mains: [{ language: chunkInfo.language, main: firstEditionID }],
            createdAt: new Date(),
            _creator: userData ? userData._id : undefined,
            __v: 1,
            status: chunkInfo.status, // Keep the status from the component
            syncState: 'pending',
          };
        });

        await this.storageService.bulkStore('chunks', chunksData);

        const contentID = chunkInfos[0].contentID;
        const contentDocs: any = await this.storageService.find('contents', [contentID]);

        if (contentDocs.length) {
          const content = contentDocs[0];
          if (insertBelowID) {
            const index = content.contents.findIndex((c) => c._id === insertBelowID || c.chunk === insertBelowID);
            if (index > -1) {
              content.contents.splice(index + 1, 0, ...chunksData);
            } else {
              content.contents.push(...chunksData);
            }
          } else {
            content.contents.push(...chunksData);
          }
          await this.storageService.store('contents', content);
        } else {
          await this.storageService.store('contents', {
            _id: contentID,
            contents: chunksData,
          });
        }
        resolve(chunksData);
      } catch (err) {
        reject(err);
      }
    });
  }

  editChunkInIndex() {}

  deleteChunkInIndex() {}

  getFileBase64(url): any {
    return new Promise((resolve, reject) => {
      this.getFileBlob(url).then((blob) => {
        const reader = new FileReader();
        reader.onloadend = function () {
          const base64data = reader.result;
          resolve(base64data);
        };
        reader.readAsDataURL(blob);
      });
    });
  }

  transcribeAudio(data: TranscriptionPayload): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        this.http.post(this.url + 'chunks/transcribe-audio', data, httpOptions).subscribe({
          next: (res: any) => {
            const output = {
              data: res.result,
              source: 'manual-transcription',
            };
            resolve(output);
          },
          error: (err) => {
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
              duration: 2000,
            });
            console.error(err);
            reject(err);
          },
        });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  }

  uploadAudioChunkStream(sessionId: string, chunkIndex: number, blob: Blob): Observable<any> {
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('chunkIndex', String(chunkIndex));
    formData.append('file', blob, `chunk_${chunkIndex}.webm`);
    return this.http.post(`${this.url}chunks/upload-stream?app_name=${this.env.appName}`, formData);
  }

  finalizeAudioChunkStream(data: {
    sessionId: string;
    duration: number;
    contentID: string;
    chunkId: string;
    language: string;
    transcribe: boolean;
    settings?: any;
  }): Observable<any> {
    return this.http.post(`${this.url}chunks/finalize-stream?app_name=${this.env.appName}`, data, httpOptions);
  }

  getFileBlob(url): any {
    return new Promise((resolve, reject) => {
      const reference = ref(this.storage, url);
      getBlob(reference).then((blob) => {
        resolve(blob);
      });
    });
  }
  getDownloadURL(url): any {
    return new Promise((resolve, reject) => {
      const reference = ref(this.storage, url);

      getDownloadURL(reference)
        .then((url) => {
          // Log the URL to see if it is localhost or a proper public URL
          console.log('Download URL:', url);
          resolve(url);
        })
        .catch((err) => {
          console.error(err, '1st level');
          let filename = url.split(environment.publicBucketStorage)[1];
          url = `gs://${environment.publicBucketStorage}${filename}`;
          const storageRef = ref(this.storage, url);
          getDownloadURL(storageRef)
            .then((url) => {
              resolve(url);
            })
            .catch((err) => {
              console.error(err, '2nd level');
              resolve('images/cancel.png');
            });
        });
    });
  }

  toQueryString(params) {
    return Object.keys(params)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
  }

  watchContent(data) {
    const userData = JSON.parse(localStorage.getItem('userData'));
    data.userID = userData.id;
    data.language = this.translate.currentLang;

    const evtSource = new EventSource(this.url + `contents/watch-content?${this.toQueryString(data)}&app_name=${this.env.appName}`);
    return evtSource;
  }

  generateAIText(data: any): Observable<{ text: string }> {
    const userData = JSON.parse(localStorage.getItem('userData'));
    data.userID = userData.id;
    data.language = this.translate.currentLang;
    return this.http.post<{ text: string }>(`${this.url}chunks/generate-ai-text`, data, httpOptions);
  }

  generateVideoProcedure(chunkId: string, contentId: string): Observable<any> {
    const data = { chunkId, contentId };
    return this.http.post(`${this.url}chunks/generate-video-procedure`, data, httpOptions);
  }

  analyzeMedia(chunkId: string, contentId: string, mediaType: 'image' | 'video'): Observable<any> {
    const data = { chunkId, contentId, mediaType };
    return this.http.post(`${this.url}chunks/analyze-media`, data, httpOptions);
  }

  generateImage(data): Observable<any> {
    const userData = JSON.parse(localStorage.getItem('userData'));
    data.user = {
      id: userData.id,
      fullName: userData.firstName + ' ' + userData.lastName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    };
    data.provider = 'gemini';
    data.returnType = 'url';
    data.linkPrefix = environment.gsLinkPrefix;
    return this.http.post(this.url + 'application/generate-image', data, httpOptions);
  }

  generateEmbeddings(data): Observable<any> {
    return this.http.post(this.url + 'chunks/generate-embeddings', data, httpOptions);
  }

  triggerGenerateEmbeddings(chunkId: string): Observable<Chunk> {
    return this.http.post<Chunk>(`${this.url}chunks/${chunkId}/generate-embeddings`, {}, httpOptions).pipe(
      tap(async (chunk: Chunk) => {
        await this.storageService.store('chunks', chunk);
      })
    );
  }

  deleteChunk(data: DeleteChunkData): Observable<any> {
    return this.http.post(this.url + 'chunks/delete/', data, httpOptions).pipe(
      tap(async (content: ServerContent) => {
        this.storageService.remove('chunks', data.chunkID);
        const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
        await this.storageService.store('contents', composedContent);
      })
    );
  }

  getImg(data): Observable<any> {
    return this.http.get(this.url + 'contents/get_img/?data=' + data, httpOptions);
  }

  deleteChunkAudio(chunk): Observable<any> {
    return this.http.post(this.url + 'chunks/delete_chunk_audio/', chunk, httpOptions);
  }

  getImgStream(data): Observable<any> {
    return this.http.get(this.url + 'contents/get_img_stream/' + data, {
      headers: new HttpHeaders({
        'Content-Type': 'application/octet-stream', //,
      }),
      responseType: 'blob',
    });
  }

  getChunkAudio(data): Observable<any> {
    return this.http.get(this.url + 'chunk_audio/?data=' + data, httpOptions);
  }

  getChunkAudioStream(data): Observable<any> {
    return this.http.get(this.url + 'chunk_audio_stream/?data=' + data, {
      headers: new HttpHeaders({
        'Content-Type': 'application/octet-stream', //,
      }),
      responseType: 'blob',
    });
  }

  setActualAudioDuration(data): Observable<any> {
    return this.http.post(this.url + 'chunks/set_actual_audio_duration/', data, httpOptions);
  }

  private generatingRequests = new Map<string, Observable<Chunk>>();

  generateAudio(data: GenerateAudioData): Observable<Chunk> {
    const key = data.chunkId;
    if (this.generatingRequests.has(key)) {
      return this.generatingRequests.get(key)!;
    }

    const userData = JSON.parse(localStorage.getItem('userData'));
    const userConfiguration: UserConfiguration = JSON.parse(localStorage.getItem('configurations'));
    data.configuration = {
      TTSAIModel: userConfiguration?.TTSAIModel,
      audioConfiguration: userConfiguration?.audioConfiguration,
    };
    data.user = {
      id: userData.id,
      fullName: userData.firstName + ' ' + userData.lastName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    };

    const req = this.http.post<Chunk>(this.url + 'chunks/generate_audio/', data, httpOptions).pipe(
      mergeMap(async (chunk: Chunk) => {
        this.storageService.store('chunks', chunk);
        if (data.contentId) {
          const res = await this.storageService.find('contents', [data.contentId]);
          if (res.length) {
            const content = { ...res[0] };
            const index = content.contents.findIndex((c) => c._id === chunk._id);
            if (index !== -1) {
              content.contents[index] = chunk;
            }
            await this.storageService.store('contents', content);
            return chunk;
          }
        }
        return chunk;
      }),
      shareReplay(1),
      finalize(() => this.generatingRequests.delete(key))
    );

    this.generatingRequests.set(key, req);
    return req;
  }

  generateAllAudios(contentId: string, language: string, chunkIds?: string[]): Observable<any> {
    const linkPrefix = environment.gsLinkPrefix;
    const userConfiguration = JSON.parse(localStorage.getItem('configurations'));
    const userData = JSON.parse(localStorage.getItem('userData'));

    const data = {
      contentId,
      targetLanguage: language,
      linkPrefix,
      configuration: {
        TTSAIModel: userConfiguration?.TTSAIModel,
        audioConfiguration: userConfiguration?.audioConfiguration,
      },
      user: {
        id: userData.id,
        fullName: userData.firstName + ' ' + userData.lastName,
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
      },
      chunkIds,
    };
    return this.http.post(`${this.url}contents/${contentId}/generate-all-audios`, data, httpOptions);
  }

  translateChunk(data: TranslateChunkData): Observable<CreateChunkResponse> {
    const userData = JSON.parse(localStorage.getItem('userData'));
    data.user = {
      id: userData.id,
      fullName: userData.firstName + ' ' + userData.lastName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    };
    return this.http.post<CreateChunkResponse>(this.url + 'chunks/tranlate-chunk/', data, httpOptions).pipe(
      tap(async (res: CreateChunkResponse) => {
        if (res) {
          const { chunk, content } = res;
          await this.storageService.store('chunks', chunk);
          if (content) {
            const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
            console.log('composedContent', composedContent);
            await this.storageService.store('contents', composedContent);
          }
        }
      })
    );
  }

  scanImage(data): Observable<any> {
    const userData = JSON.parse(localStorage.getItem('userData'));
    data.user = {
      id: userData.id,
      fullName: userData.firstName + ' ' + userData.lastName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    };
    return this.http.post(this.url + 'chunks/scan-image', data, httpOptions);
  }

  checkEditPermission(data): Observable<any> {
    return this.http.post(this.url + 'chunks/checkPermission', data, httpOptions);
  }

  generateChunkEdition(req): Observable<any> {
    return this.http.patch(this.url + 'chunks/generate/editions/?id=' + req.id, req, httpOptions);
  }

  setAudioDuration(req): Observable<any> {
    return this.http.post(this.url + 'chunks/set-audio-duration', req, httpOptions);
  }

  deleteChunkEdition(req): Observable<any> {
    return this.http.patch(this.url + 'chunks/delete/editions/?id=' + req.id, req, httpOptions);
  }

  setMainChunkEdition(req): Observable<any> {
    return this.http.patch(this.url + 'chunks/set_main/editions/?id=' + req.id, req, httpOptions);
  }

  addTag(data): Observable<any> {
    return this.http.post(this.url + 'chunks/add-tag/', data, httpOptions).pipe(tap((res) => this.storageService.update('chunks', res._id, res)));
  }

  createChunkFromGCSCopy(data: CopyImageAndCreateChunkData): Observable<CreateChunkResponse> {
    return this.http.post<CreateChunkResponse>(this.url + 'chunks/copy-image', data, httpOptions).pipe(
      tap(async (res: CreateChunkResponse) => {
        const { chunk, content } = res;
        await this.storageService.store('chunks', chunk);
        const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
        this.storageService.store('contents', composedContent);
      })
    );
  }

  removeTag(data): Observable<any> {
    return this.http.post(this.url + `chunks/remove-tag`, data, httpOptions).pipe(
      tap((res) => {
        this.storageService.update('chunks', res._id, res);
      })
    );
  }

  removeAllTags(data): Observable<any> {
    return this.http.post(this.url + `chunks/remove-all-tags`, data, httpOptions).pipe(
      tap((res) => {
        this.storageService.update('chunks', res._id, res);
      })
    );
  }

  // Added from app-old.service.ts
  getChunkVideo(id): Observable<any> {
    return this.http.get(this.url + 'chunk_video/?id=' + id, httpOptions);
  }

  generateSpeechSample(data: GenerateSpeechData): Observable<Blob> {
    const userData = JSON.parse(localStorage.getItem('userData'));
    data.user = {
      id: userData.id,
      fullName: userData.firstName + ' ' + userData.lastName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    };
    return this.http.post(this.url + 'chunks/generate-speech', data, { responseType: 'blob' });
  }
}
